import { ReactNode } from 'react';
import { AuthenticatorConfiguration } from '@mews-commander/data-access';
import { HighlightIcon } from '@mews/b2b-ui';
import { LocalizedText, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    IconName,
    Image,
    Link,
    LinkProps,
    SkeletonCard,
    SkeletonText,
    Stack,
    StackItem,
    Typography,
} from '@optimus-web/core';
import { Configure2FADeviceConfigManual } from './Configure2FADeviceConfigManual';

export interface Configure2FADeviceConfigContentProps {
    authenticator?: AuthenticatorConfiguration;
    isPending: boolean;
}

const authenticatorLinks = {
    google: 'https://support.google.com/accounts/answer/1066447',
    microsoft:
        'https://support.microsoft.com/en-us/account-billing/set-up-temporary-verification-codes-in-the-microsoft-authenticator-app-55a60b6e-804c-436e-8884-644fcd94bbc21',
    passwordManager: 'https://support.1password.com/one-time-passwords/',
};

export function Configure2FADeviceConfigContent({ authenticator, isPending }: Configure2FADeviceConfigContentProps) {
    const [{ active, onClose }, toggleDialog] = useDialog();
    return (
        <>
            <Stack spacing="300" vertical as="ul">
                <ConfigureStep
                    title={<LocalizedText textKey={TextKey.TwoFactorAuthenticationAppDownloadStepTitle} />}
                    subtitle={
                        <LocalizedText
                            textKey={TextKey.TwoFactorAuthenticationAppDownloadStepDescription}
                            substitutes={{
                                googleAuthLink: (text) => (
                                    <AuthenticatorLink href={authenticatorLinks.google}>{text}</AuthenticatorLink>
                                ),
                                microsoftAuthLink: (text) => (
                                    <AuthenticatorLink href={authenticatorLinks.microsoft}>{text}</AuthenticatorLink>
                                ),
                                passwordManagerLink: (text) => (
                                    <AuthenticatorLink href={authenticatorLinks.passwordManager}>
                                        {text}
                                    </AuthenticatorLink>
                                ),
                            }}
                        />
                    }
                    icon={IconName.Phone}
                />
                <ConfigureStep
                    title={<LocalizedText textKey={TextKey.TwoFactorAuthenticationAppScanQRStepTitle} />}
                    subtitle={<LocalizedText textKey={TextKey.TwoFactorAuthenticationAppScanQRStepDescription} />}
                    icon={IconName.ScanQr}
                >
                    <CantScanCodeLink onClick={toggleDialog} authenticator={authenticator} isPending={isPending} />
                </ConfigureStep>
                <Stack alignment="center">
                    <QRCode authenticator={authenticator} isPending={isPending} />
                </Stack>
            </Stack>

            {authenticator?.manualCode && (
                <Configure2FADeviceConfigManual
                    manualCode={authenticator.manualCode}
                    active={active}
                    onClose={onClose}
                />
            )}
        </>
    );
}

interface Configure2FADeviceConfigQRProps {
    authenticator: AuthenticatorConfiguration | undefined;
    isPending: boolean;
}

const CantScanCodeLink = ({
    onClick,
    authenticator,
    isPending,
}: Pick<LinkProps, 'onClick'> & Configure2FADeviceConfigQRProps) => {
    if (!authenticator || isPending) {
        return <SkeletonText />;
    }

    return (
        <Link onClick={onClick} variant="basic" size="small" standalone>
            <Typography textStyle="bodySmall" variant="secondary" as="div">
                <LocalizedText textKey={TextKey.CannotScanQRCode} />
            </Typography>
        </Link>
    );
};

const QR_CODE_SIZE = '120px';

const QRCode = ({ authenticator, isPending }: Configure2FADeviceConfigQRProps) => {
    const qrCodeUrl = authenticator?.qrCodeUrl;
    const isReady = qrCodeUrl && !isPending;
    return (
        <SkeletonCard style={{ width: QR_CODE_SIZE, height: QR_CODE_SIZE }}>
            {isReady && <Image src={qrCodeUrl} width={QR_CODE_SIZE} height={QR_CODE_SIZE} />}
        </SkeletonCard>
    );
};

const AuthenticatorLink = ({ href, children }: Pick<LinkProps, 'href' | 'children'>) => (
    <Link href={href} variant="basic" target="_blank" rel="noreferrer">
        {children}
    </Link>
);

const ConfigureStep = ({
    title,
    subtitle,
    icon,
    children,
}: {
    title: ReactNode;
    subtitle: ReactNode;
    icon: IconName;
    children?: React.ReactNode;
}) => (
    <Stack spacing="200" as="li">
        <StackItem contentFit>
            <HighlightIcon icon={icon} />
        </StackItem>

        <Stack spacing="100" vertical>
            <Typography textStyle="bodyMediumStrong" as="h3">
                {title}
            </Typography>
            <Typography textStyle="bodySmall" variant="secondary" as="p">
                {subtitle}
            </Typography>
            {children}
        </Stack>
    </Stack>
);
